'use client';

import React, { useMemo, useState } from 'react';
import {
  addDays,
  addMonths,
  addWeeks,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isToday,
  startOfMonth,
  startOfWeek,
  subMonths,
  subWeeks,
} from 'date-fns';
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp } from 'lucide-react';

interface CalendarStyleDatePickerProps {
  selectedDate: Date;
  onSelectDate: (date: Date) => void;
  primaryColor: string;
  isDark: boolean;
  markerDates?: Set<string>;
  pendingMarkerDates?: Set<string>;
  minDate?: Date;
  maxDate?: Date;
  className?: string;
}

const toKey = (date: Date) => format(date, 'yyyy-MM-dd');

export default function CalendarStyleDatePicker({
  selectedDate,
  onSelectDate,
  primaryColor,
  isDark,
  markerDates,
  pendingMarkerDates,
  minDate,
  maxDate,
  className = '',
}: CalendarStyleDatePickerProps) {
  const [expanded, setExpanded] = useState(false);
  const [monthCursor, setMonthCursor] = useState<Date>(startOfMonth(selectedDate));

  const isDateDisabled = (date: Date) => {
    const key = toKey(date);
    if (minDate && key < toKey(minDate)) {
      return true;
    }

    if (maxDate && key > toKey(maxDate)) {
      return true;
    }

    return false;
  };

  const weekDays = useMemo(() => {
    const weekStart = startOfWeek(selectedDate, { weekStartsOn: 0 });

    return Array.from({ length: 7 }, (_, index) => {
      const date = addDays(weekStart, index);
      return {
        date,
        key: toKey(date),
        dayLabel: format(date, 'EE'),
        dayNumber: format(date, 'd'),
        isCurrentDay: isToday(date),
        hasMarker: markerDates?.has(toKey(date)) ?? false,
        hasPendingMarker: pendingMarkerDates?.has(toKey(date)) ?? false,
      };
    });
  }, [markerDates, pendingMarkerDates, selectedDate]);

  const monthCells = useMemo(() => {
    const firstCell = startOfWeek(startOfMonth(monthCursor), { weekStartsOn: 0 });
    const lastCell = endOfWeek(endOfMonth(monthCursor), { weekStartsOn: 0 });

    const cells: Date[] = [];
    let cursor = firstCell;
    while (cursor <= lastCell) {
      cells.push(cursor);
      cursor = addDays(cursor, 1);
    }

    return cells;
  }, [monthCursor]);

  const shiftSelectedWeek = (direction: -1 | 1) => {
    onSelectDate(direction > 0 ? addWeeks(selectedDate, 1) : subWeeks(selectedDate, 1));
  };

  return (
    <div className={`rounded-2xl border p-2 shadow-sm ${isDark ? 'border-gray-700/80 bg-gray-900/60' : 'border-gray-200/90 bg-white/90'} ${className}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <div className={`inline-flex items-center gap-1 rounded-xl border p-1 ${isDark ? 'border-gray-700 bg-gray-800/90' : 'border-gray-200 bg-gray-50/95'}`}>
          <button
            type="button"
            onClick={() => shiftSelectedWeek(-1)}
            className="p-1.5 rounded-lg text-slate-600 transition-colors hover:bg-slate-100/80 dark:text-slate-300 dark:hover:bg-slate-700/80"
            title="Previous week"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={() => onSelectDate(new Date())}
            className="rounded-lg px-2.5 py-1.5 text-xs font-semibold shadow-sm"
            style={{ backgroundColor: `${primaryColor}18`, color: primaryColor }}
          >
            Today
          </button>
          <button
            type="button"
            onClick={() => shiftSelectedWeek(1)}
            className="p-1.5 rounded-lg text-slate-600 transition-colors hover:bg-slate-100/80 dark:text-slate-300 dark:hover:bg-slate-700/80"
            title="Next week"
          >
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>

        <button
          type="button"
          onClick={() => {
            setExpanded((prev) => !prev);
            setMonthCursor(startOfMonth(selectedDate));
          }}
          className={`inline-flex items-center gap-1 rounded-xl border px-2.5 py-1.5 text-xs font-medium shadow-sm transition-colors ${isDark ? 'border-gray-700 bg-gray-800/90 text-gray-300 hover:bg-gray-700/80' : 'border-gray-200 bg-gray-50/95 text-slate-600 hover:bg-slate-100/90'}`}
        >
          {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          {expanded ? 'Hide' : 'Calendar'}
        </button>
      </div>

      <div className={`grid grid-cols-7 gap-1 rounded-2xl border p-1.5 ${isDark ? 'border-gray-700 bg-gray-800/70' : 'border-gray-200 bg-gray-50/90'}`}>
        {weekDays.map((day) => {
          const disabled = isDateDisabled(day.date);
          const active = isSameDay(selectedDate, day.date);

          return (
            <button
              key={day.key}
              type="button"
              disabled={disabled}
              onClick={() => onSelectDate(day.date)}
              className={`flex min-w-0 flex-col items-center rounded-xl py-2 transition-all ${
                active
                  ? 'text-white shadow-sm'
                  : day.isCurrentDay
                  ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-sky-300'
                  : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'
              } ${disabled ? 'opacity-35 cursor-not-allowed' : ''}`}
              style={active ? { backgroundColor: primaryColor } : undefined}
            >
              <span className="text-[10px] font-medium">{day.dayLabel}</span>
              <span className="text-sm font-bold leading-tight">{day.dayNumber}</span>
              {day.hasPendingMarker && !active && !disabled && (
                <div className="mt-1 h-1.5 w-1.5 rounded-full" style={{ backgroundColor: '#f59e0b' }} />
              )}
              {day.hasMarker && !day.hasPendingMarker && !active && !disabled && (
                <div className="mt-1 h-1.5 w-1.5 rounded-full" style={{ backgroundColor: primaryColor }} />
              )}
            </button>
          );
        })}
      </div>

      {expanded && (
        <div className={`mt-2 rounded-2xl border p-2 ${isDark ? 'border-gray-700 bg-gray-800/70' : 'border-gray-200 bg-gray-50/90'}`}>
          <div className="mb-2 flex items-center justify-between">
            <button
              type="button"
              onClick={() => setMonthCursor((prev) => subMonths(prev, 1))}
              className="p-1.5 rounded-lg text-slate-600 transition-colors hover:bg-slate-100/80 dark:text-slate-300 dark:hover:bg-slate-700/80"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
            </button>
            <div className="text-sm font-semibold text-gray-900 dark:text-white">
              {format(monthCursor, 'MMMM yyyy')}
            </div>
            <button
              type="button"
              onClick={() => setMonthCursor((prev) => addMonths(prev, 1))}
              className="p-1.5 rounded-lg text-slate-600 transition-colors hover:bg-slate-100/80 dark:text-slate-300 dark:hover:bg-slate-700/80"
            >
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1 mb-1">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((label) => (
              <div key={label} className="text-[10px] text-center font-medium text-gray-500 dark:text-gray-400 py-1">
                {label}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {monthCells.map((date) => {
              const key = toKey(date);
              const active = isSameDay(date, selectedDate);
              const inCurrentMonth = date.getMonth() === monthCursor.getMonth();
              const hasMarker = markerDates?.has(key) ?? false;
              const hasPendingMarker = pendingMarkerDates?.has(key) ?? false;
              const disabled = isDateDisabled(date);

              return (
                <button
                  key={key}
                  type="button"
                  disabled={disabled}
                  onClick={() => onSelectDate(date)}
                  className={`relative rounded-lg py-1.5 text-xs transition-colors ${
                    active
                      ? 'text-white shadow-sm'
                      : inCurrentMonth
                      ? 'text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700'
                      : 'text-gray-400 hover:bg-gray-100 dark:text-gray-500 dark:hover:bg-gray-700'
                  } ${disabled ? 'opacity-35 cursor-not-allowed' : ''}`}
                  style={active ? { backgroundColor: primaryColor } : undefined}
                >
                  {format(date, 'd')}
                  {hasPendingMarker && !active && !disabled && (
                    <span
                      className="absolute bottom-0.5 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full"
                      style={{ backgroundColor: '#f59e0b' }}
                    />
                  )}
                  {hasMarker && !hasPendingMarker && !active && !disabled && (
                    <span
                      className="absolute bottom-0.5 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full"
                      style={{ backgroundColor: primaryColor }}
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
